import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../Constants";
import { AuthContext } from "../context/AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EmailConfig = () => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    companyId: user?.id,
    host: "",
    port: "",
    username: "",
    password: "",
    auth: true,
    starttls: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isExistingConfig, setIsExistingConfig] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch existing config when component mounts
  useEffect(() => {
    const fetchSmtpConfig = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/smtpConfig/company/${user?.id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.data) {
          setFormData({
            ...response.data,
            port: response.data.port.toString(), // Convert port to string for input
          });
          setIsExistingConfig(true);
        }
      } catch (err) {
        // Config doesn't exist yet, ignore error
      }
    };

    fetchSmtpConfig();
  }, [user?.id]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "select-one" ? value === "true" : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Convert port to number
      const payload = {
        ...formData,
        port: Number(formData.port),
      };

      let response;
      if (isExistingConfig) {
        // Update existing config
        response = await axios.put(
          `${API_URL}/api/smtpConfig/update/${user.id}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        // Create new config
        response = await axios.post(`${API_URL}/api/smtpConfig`, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }

      if (response.data.success) {
        setSuccess(true);
        setIsExistingConfig(true); // Mark as existing config after successful save
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          (isExistingConfig
            ? "Failed to update SMTP configuration"
            : "Failed to save SMTP configuration")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/smsConfig/delete/${user?.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("URL deleted successfully.");
      setIsExistingConfig(false);
      setShowDeleteModal(false);
    } catch (error) {
      toast.error("Failed to delete URL.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className=" mx-auto p-4 bg-[#FAF8FF] dark:bg-gray-900 rounded-lg shadow-lg cfg-font">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
      <h2 className="text-2xl font-bold cfg-heading text-gray-800 dark:text-white mb-6">
        SMTP Configuration
      </h2>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 dark:bg-red-900 dark:border-red-700 dark:text-red-100">
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-100 border-l-4 border-green-500 text-green-700 dark:bg-green-900 dark:border-green-700 dark:text-green-100">
          <p>
            SMTP configuration {isExistingConfig ? "updated" : "saved"}{" "}
            successfully!
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Server Address */}
          <div className="sm:col-span-2">
            <label
              htmlFor="host"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Server Address (Host)
            </label>
            <input
              type="text"
              id="host"
              name="host"
              value={formData.host}
              onChange={handleChange}
              placeholder="Enter the address of your SMTP server (e.g., smtp.gmail.com for Gmail)"
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:ring-[#8B5CF6] focus:border-[#673AB7] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>

          {/* Port */}
          <div>
            <label
              htmlFor="port"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Port
            </label>
            <input
              type="number"
              id="port"
              name="port"
              value={formData.port}
              onChange={handleChange}
              placeholder="Enter the port number (e.g., 587 for Gmail with TLS)"
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:ring-[#8B5CF6] focus:border-[#673AB7] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>

          {/* Username */}
          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter the username for your email account"
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:ring-[#8B5CF6] focus:border-[#673AB7] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>

          {/* Password */}
          <div className="sm:col-span-2 relative">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your app password (don't have? configure the SMTP app password)"
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:ring-[#8B5CF6] focus:border-[#673AB7] dark:bg-gray-700 dark:border-gray-600 dark:text-white pr-10"
              required
            />
            <button
  type="button"
  onClick={() => setShowPassword(!showPassword)}
  className="absolute top-9 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
  tabIndex={-1}
>
  {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
</button>
          </div>

          {/* Auth Dropdown */}
          <div>
            <label
              htmlFor="auth"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Authentication Required
            </label>
            <select
              id="auth"
              name="auth"
              value={formData.auth.toString()}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:ring-[#8B5CF6] focus:border-[#673AB7] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
          </div>

          {/* STARTTLS Dropdown */}
          <div>
            <label
              htmlFor="starttls"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Use STARTTLS
            </label>
            <select
              id="starttls"
              name="starttls"
              value={formData.starttls.toString()}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-md focus:ring-[#8B5CF6] focus:border-[#673AB7] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#673AB7] hover:bg-[#5E35A6] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8B5CF6] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white "
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {isExistingConfig ? "Updating..." : "Saving..."}
              </span>
            ) : isExistingConfig ? (
              "Update Configuration"
            ) : (
              "Save Configuration"
            )}
            </button>

            {isExistingConfig && (
          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            Delete 
          </button>
        )}

        </div>
      </form>
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4 cfg-heading text-gray-800 dark:text-white">
              Confirm Delete
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Are you sure you want to delete this URL?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailConfig;
